// components/DailyLogsTab.js
import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import {
  TrashIcon,
  PencilIcon,
  CloudIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { LuWind } from "react-icons/lu";
import { FaTemperatureLow } from "react-icons/fa6";
import { WiRaindrop } from "react-icons/wi";
import Select from "react-select";
import AlertDialog from "./AlertDialog";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}
// logDate is stored as UTC midnight; read it back using UTC getters
// so it always displays as the date that was actually selected,
// regardless of the viewer's local timezone.
function formatLogDate(dateInput, pattern = "MMM dd, yyyy") {
  const d = new Date(dateInput);
  const utcAsLocal = new Date(
    d.getUTCFullYear(),
    d.getUTCMonth(),
    d.getUTCDate(),
  );
  return format(utcAsLocal, pattern);
}

export default function DailyLogsTab({ project, setProject }) {
  const navigate = useNavigate();
  const [dailyLogs, setDailyLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentWeather, setCurrentWeather] = useState(null);
  const [formData, setFormData] = useState({
    logDate: format(new Date(), "yyyy-MM-dd"),
    siteConditions: "clear",
    siteConditionsDescription: "",
    crew: [],
    workCompletedDescription: "",
    media: [],
  });
  const [crewOptions, setCrewOptions] = useState([]);
  const [showForm, setShowForm] = useState(false); // Control form visibility
  const [deleteAlert, setDeleteAlert] = useState({
    open: false,
    dailyLogId: null,
    title: "",
    message: "",
  });
  const [successAlert, setSuccessAlert] = useState({
    open: false,
    title: "",
    message: "",
  });

  // Custom styles for react-select
  const selectStyles = {
    control: (provided) => ({
      ...provided,
      backgroundColor: "#1F2937",
      borderColor: "#4B5563",
      color: "#E5E7EB",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#F97316",
      },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: "#1F2937",
      color: "#E5E7EB",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? "#F97316" : "#1F2937",
      color: "#E5E7EB",
      "&:hover": {
        backgroundColor: "#374151",
      },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#374151",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#E5E7EB",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "#E5E7EB",
      "&:hover": {
        backgroundColor: "#EF4444",
        color: "#FFFFFF",
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#9CA3AF",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#E5E7EB",
    }),
  };

  // Fetch daily logs
  useEffect(() => {
    const fetchDailyLogs = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `https://erp-production-72da01c8e651.herokuapp.com/api/dailyLogs/${project._id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setDailyLogs(response.data.dailyLogs);
        setLoading(false);
      } catch (err) {
        setError(
          "Failed to load daily logs: " +
            (err.response?.data?.message || err.message),
        );
        setLoading(false);
      }
    };
    fetchDailyLogs();
  }, [project._id]);

  // Fetch crew options and last log for crew preloading
  useEffect(() => {
    const fetchCrewAndData = async () => {
      try {
        const token = localStorage.getItem("token");

        // Fetch all users for crew selection
        const usersResponse = await axios.get(
          `https://erp-production-72da01c8e651.herokuapp.com/api/users`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const crewOptionsFormatted = usersResponse.data.map((user) => ({
          value: user._id,
          label: user.displayName,
        }));
        setCrewOptions(crewOptionsFormatted);

        // Fetch last daily log to preload crew
        const lastLogResponse = await axios.get(
          `https://erp-production-72da01c8e651.herokuapp.com/api/dailyLogs/${project._id}/last`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (lastLogResponse.data.lastDailyLog) {
          setFormData((prev) => ({
            ...prev,
            crew: lastLogResponse.data.lastDailyLog.crew.map((c) => c._id),
          }));
        }
      } catch (err) {
        console.error("Error fetching crew or last log:", err);
        setError(
          "Failed to load crew data: " +
            (err.response?.data?.message || err.message),
        );
      }
    };
    fetchCrewAndData();
  }, [project._id]);

  // Fetch weather when form is shown
  useEffect(() => {
    if (showForm) {
      const fetchWeather = async () => {
        try {
          const weatherResponse = await axios.get(
            `https://api.open-meteo.com/v1/forecast?latitude=${project.address.latitude}&longitude=${project.address.longitude}&current_weather=true&hourly=relativehumidity_2m,windspeed_10m`,
          );
          const currentWeather = weatherResponse.data.current_weather;
          const currentHour = new Date().getHours();
          const weatherCodeMap = {
            0: "Clear",
            1: "Mainly Clear",
            2: "Partly Cloudy",
            3: "Overcast",
            45: "Fog",
            48: "Depositing Rime Fog",
            51: "Light Drizzle",
            53: "Moderate Drizzle",
            55: "Dense Drizzle",
            61: "Light Rain",
            63: "Moderate Rain",
            65: "Heavy Rain",
            71: "Light Snow",
            73: "Moderate Snow",
            75: "Heavy Snow",
            77: "Snow Grains",
            80: "Light Rain Showers",
            81: "Moderate Rain Showers",
            82: "Violent Rain Showers",
            85: "Light Snow Showers",
            86: "Heavy Snow Showers",
            95: "Thunderstorm",
            96: "Thunderstorm with Light Hail",
            99: "Thunderstorm with Heavy Hail",
          };
          setCurrentWeather({
            temperature: currentWeather.temperature,
            condition: weatherCodeMap[currentWeather.weathercode] || "Unknown",
            humidity:
              weatherResponse.data.hourly.relativehumidity_2m[currentHour] || 0,
            windSpeed: currentWeather.windspeed,
          });
        } catch (err) {
          console.error("Error fetching weather:", err);
          setError("Failed to load weather data: " + err.message);
        }
      };
      fetchWeather();
    }
  }, [showForm, project.address.latitude, project.address.longitude]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCrewChange = (selectedOptions) => {
    setFormData((prev) => ({
      ...prev,
      crew: selectedOptions
        ? selectedOptions.map((option) => option.value)
        : [],
    }));
  };

  const handleMediaChange = (e) => {
    setFormData((prev) => ({ ...prev, media: e.target.files }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const formDataToSend = new FormData();
      formDataToSend.append("logDate", formData.logDate);
      formDataToSend.append("siteConditions", formData.siteConditions);
      formDataToSend.append(
        "siteConditionsDescription",
        formData.siteConditionsDescription,
      );
      formDataToSend.append("crew", JSON.stringify(formData.crew));
      formDataToSend.append(
        "workCompletedDescription",
        formData.workCompletedDescription,
      );
      for (let i = 0; i < formData.media.length; i++) {
        formDataToSend.append("media", formData.media[i]);
      }

      const response = await axios.post(
        `https://erp-production-72da01c8e651.herokuapp.com/api/dailyLogs/${project._id}`,
        formDataToSend,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      setDailyLogs((prev) => [response.data.dailyLog, ...prev]);
      setFormData({
        logDate: format(new Date(), "yyyy-MM-dd"),
        siteConditions: "clear",
        siteConditionsDescription: "",
        crew: formData.crew, // Retain crew for next entry
        workCompletedDescription: "",
        media: [],
      });
      setProject((prev) => ({
        ...prev,
        dailyLogs: [...(prev.dailyLogs || []), response.data.dailyLog._id],
      }));
      setShowForm(false); // Hide form after submission
      setSuccessAlert({
        open: true,
        title: "Success",
        message: "Daily log created successfully!",
      });
    } catch (err) {
      setError(
        "Failed to create daily log: " +
          (err.response?.data?.message || err.message),
      );
    }
  };

  const handleDeleteConfirm = (dailyLogId) => {
    setDeleteAlert({
      open: true,
      dailyLogId,
      title: "Confirm Delete",
      message:
        "Are you sure you want to delete this daily log? This action cannot be undone.",
    });
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `https://erp-production-72da01c8e651.herokuapp.com/api/dailyLogs/${deleteAlert.dailyLogId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setDailyLogs((prev) =>
        prev.filter((log) => log._id !== deleteAlert.dailyLogId),
      );
      setProject((prev) => ({
        ...prev,
        dailyLogs: prev.dailyLogs.filter((id) => id !== deleteAlert.dailyLogId),
      }));
      setDeleteAlert({ open: false, dailyLogId: null, title: "", message: "" });
    } catch (err) {
      setError(
        "Failed to delete daily log: " +
          (err.response?.data?.message || err.message),
      );
    }
  };

  if (loading)
    return <div className="text-center py-4 text-gray-200">Loading...</div>;
  if (error)
    return <div className="text-red-600 text-center py-4">{error}</div>;

  return (
    <div className="bg-gray-800 rounded-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-white bg-gray-700 rounded-md px-4 py-2">
          Daily Logs
        </h2>
        <button
          onClick={() => setShowForm(true)}
          className="bg-orange-400 text-white px-6 py-2 rounded-md hover:bg-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 focus:ring-offset-gray-700 transition-colors"
        >
          Create New Log
        </button>
      </div>

      {/* Current Weather Display */}
      {currentWeather && showForm && (
        <div className="mb-6 bg-gray-800 rounded-lg p-4 shadow-md">
          <h3 className="text-md font-medium text-gray-200 mb-3">
            Current Weather
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="flex items-center space-x-2">
              <FaTemperatureLow className="h-6 w-6 text-orange-400" />
              <div>
                <p className="text-sm font-medium text-gray-400">Temperature</p>
                <p className="text-lg text-gray-200">
                  {currentWeather.temperature}°C
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <CloudIcon className="h-6 w-6 text-orange-400" />
              <div>
                <p className="text-sm font-medium text-gray-400">Condition</p>
                <p className="text-lg text-gray-200">
                  {currentWeather.condition}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <WiRaindrop className="h-6 w-6 text-orange-400" />
              <div>
                <p className="text-sm font-medium text-gray-400">Humidity</p>
                <p className="text-lg text-gray-200">
                  {currentWeather.humidity}%
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <LuWind className="h-6 w-6 text-orange-400" />
              <div>
                <p className="text-sm font-medium text-gray-400">Wind Speed</p>
                <p className="text-lg text-gray-200">
                  {currentWeather.windSpeed} km/h
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form for creating a new daily log */}
      {showForm && (
        <form
          onSubmit={handleSubmit}
          className="mb-6 bg-gray-700 p-6 rounded-lg shadow-md"
        >
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-md font-medium text-gray-200">
              Create Daily Log
            </h3>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="text-gray-400 hover:text-gray-200"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-1">
                Log Date
              </label>
              <input
                type="date"
                name="logDate"
                value={formData.logDate}
                onChange={handleInputChange}
                className="block w-full rounded-md bg-gray-800 text-gray-200 border-gray-600 focus:border-orange-400 focus:ring-orange-400 focus:ring-2 focus:outline-none p-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-1">
                Site Conditions
              </label>
              <select
                name="siteConditions"
                value={formData.siteConditions}
                onChange={handleInputChange}
                className="block w-full rounded-md bg-gray-800 text-gray-200 border-gray-600 focus:border-orange-400 focus:ring-orange-400 focus:ring-2 focus:outline-none p-2"
                required
              >
                <option value="clear">Clear</option>
                <option value="muddy">Muddy</option>
                <option value="dusty">Dusty</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-200 mb-1">
                Site Conditions Description
              </label>
              <textarea
                name="siteConditionsDescription"
                value={formData.siteConditionsDescription}
                onChange={handleInputChange}
                className="block w-full rounded-md bg-gray-800 text-gray-200 border-gray-600 focus:border-orange-400 focus:ring-orange-400 focus:ring-2 focus:outline-none p-2"
                rows="3"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-200 mb-1">
                Crew on Site
              </label>
              <Select
                isMulti
                options={crewOptions}
                value={crewOptions.filter((option) =>
                  formData.crew.includes(option.value),
                )}
                onChange={handleCrewChange}
                styles={selectStyles}
                placeholder="Select crew members..."
                className="text-gray-200"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-200 mb-1">
                Work Completed Description
              </label>
              <textarea
                name="workCompletedDescription"
                value={formData.workCompletedDescription}
                onChange={handleInputChange}
                className="block w-full rounded-md bg-gray-800 text-gray-200 border-gray-600 focus:border-orange-400 focus:ring-orange-400 focus:ring-2 focus:outline-none p-2"
                rows="5"
                required
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-200 mb-1">
                Upload Photos/Videos
              </label>
              <input
                type="file"
                multiple
                accept="image/*,video/*"
                onChange={handleMediaChange}
                className="block w-full text-gray-200 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-orange-400 file:text-white file:hover:bg-orange-500"
              />
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="bg-gray-600 text-gray-200 px-6 py-2 rounded-md hover:bg-gray-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-orange-400 text-white px-6 py-2 rounded-md hover:bg-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 focus:ring-offset-gray-700 transition-colors"
            >
              Submit Daily Log
            </button>
          </div>
        </form>
      )}

      {/* Daily Logs Table */}
      <div className="overflow-hidden rounded-lg shadow-xl">
        <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
          <thead className="bg-gray-800">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider rounded-tl-lg">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Created By
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Weather
              </th>

              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Crew
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider rounded-tr-lg">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-600">
            {dailyLogs.map((log) => (
              <tr
                key={log._id}
                className="hover:bg-gray-600 transition-colors duration-200"
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                  {formatLogDate(log.logDate)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                  {log.createdBy?.displayName || "N/A"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                  {log.weather?.condition
                    ? `${log.weather.condition}, ${log.weather.temperature}°C, ${log.weather.humidity}%`
                    : "N/A"}
                </td>

                <td className="px-6 py-4 text-sm text-gray-200">
                  {log.crew.map((c) => c.displayName).join(", ") || "None"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right flex space-x-2 justify-end">
                  <button
                    onClick={() => navigate(`/daily-logs/${log._id}`)}
                    className="inline-flex items-center rounded-full bg-blue-100 px-4 py-0.5 text-sm font-medium text-gray-900 hover:bg-blue-200 transition-colors"
                  >
                    <PencilIcon className="h-4 w-4 mr-1" />
                    View/Edit
                  </button>
                  <button
                    onClick={() => handleDeleteConfirm(log._id)}
                    className="inline-flex items-center rounded-full bg-red-100 px-4 py-0.5 text-sm font-medium text-red-700 hover:bg-red-200 transition-colors"
                  >
                    <TrashIcon className="h-4 w-4 mr-1" />
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {dailyLogs.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="px-6 py-4 text-center text-sm text-gray-400"
                >
                  No daily logs found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <AlertDialog
        open={deleteAlert.open}
        type="warning"
        title={deleteAlert.title}
        message={deleteAlert.message}
        onClose={() =>
          setDeleteAlert({
            open: false,
            dailyLogId: null,
            title: "",
            message: "",
          })
        }
        onConfirm={handleDelete}
        showCancel={true}
      />
      <AlertDialog
        open={successAlert.open}
        type="success"
        title={successAlert.title}
        message={successAlert.message}
        onClose={() => setSuccessAlert({ open: false, title: "", message: "" })}
        onConfirm={() =>
          setSuccessAlert({ open: false, title: "", message: "" })
        }
        confirmText="OK"
        showCancel={false}
      />
    </div>
  );
}
